<template>
  <v-container fluid class="retouren-v2-container pa-4">
    <!-- ========================================
         TOOLBAR & NAVIGATION
         ======================================== -->
    <v-toolbar color="transparent" class="mb-4">
      <v-toolbar-title class="text-h5">
        <v-icon class="mr-2">mdi-package-variant-closed</v-icon>
        Retouren Sorting V2
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        prepend-icon="mdi-plus"
        @click="openNewHeaderDialog"
        elevation="2"
      >
        Neuer Lieferschein
      </v-btn>
    </v-toolbar>

    <!-- ========================================
         HEADER-LISTE (Übersicht aller Lieferscheine)
         ======================================== -->
    <v-card v-if="!selectedHeader" class="mb-4 overview-card">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
        Meine Lieferscheine
        <v-spacer></v-spacer>
        <v-chip-group v-model="statusFilter" mandatory>
          <v-chip value="alle" filter>Alle</v-chip>
          <v-chip value="offen" filter>Offen</v-chip>
          <v-chip value="in_bearbeitung" filter>In Bearbeitung</v-chip>
          <v-chip value="abgeschlossen" filter>Abgeschlossen</v-chip>
        </v-chip-group>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-data-table
          :headers="headerTableHeaders"
          :items="filteredHeaders"
          :loading="loading.headers"
          item-value="id"
          class="elevation-0"
        >
          <template v-slot:[`item.lieferschein_nr`]="{ item }">
            <strong>{{ item.lieferschein_nr || '—' }}</strong>
          </template>
          <template v-slot:[`item.kunde`]="{ item }">
            {{ item.kunde || '—' }}
          </template>
          <template v-slot:[`item.datum_pruefung`]="{ item }">
            {{ formatDate(item.datum_pruefung) }}
          </template>
          <template v-slot:[`item.anzahl_positionen`]="{ item }">
            <v-chip size="small" color="info">
              {{ item.anzahl_positionen }} Positionen
            </v-chip>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
              variant="flat"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              @click="loadHeader(item.id)"
              title="Bearbeiten"
            ></v-btn>
            <v-btn
              icon="mdi-printer"
              size="small"
              variant="text"
              color="primary"
              @click="exportToPDF(item.id)"
              title="Als PDF drucken"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click="confirmDeleteHeader(item.id)"
              title="Löschen"
            ></v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- ========================================
         DETAIL-ANSICHT (Bearbeitung eines Lieferscheins)
         ======================================== -->
    <div v-if="selectedHeader">
      <!-- Zurück-Button -->
      <v-btn
        class="mb-4"
        prepend-icon="mdi-arrow-left"
        @click="backToList"
      >
        Zurück zur Übersicht
      </v-btn>

      <!-- Header-Informationen -->
      <v-card class="mb-4 header-info-card">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-file-document-outline</v-icon>
          Lieferschein: {{ selectedHeader.lieferschein_nr }}
          <v-spacer></v-spacer>
          <v-chip
            :color="getStatusColor(selectedHeader.status)"
            variant="flat"
          >
            {{ getStatusText(selectedHeader.status) }}
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="selectedHeader.kunde"
                label="Kunde"
                variant="outlined"
                density="comfortable"
                @blur="updateHeader"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="selectedHeader.lieferschein_nr"
                label="Lieferschein-Nr."
                variant="outlined"
                density="comfortable"
                @blur="updateHeader"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="selectedHeader.eingangart_datum"
                label="Eingangart/Datum (z.B. SPEDI 11.12.2025)"
                variant="outlined"
                density="comfortable"
                @blur="updateHeader"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="selectedHeader.pruefer_name"
                label="Prüfer (Name)"
                variant="outlined"
                density="comfortable"
                readonly
                prepend-inner-icon="mdi-account"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="selectedHeader.pruefer_mitarbeiternummer"
                label="Mitarbeiternummer"
                variant="outlined"
                density="comfortable"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="selectedHeader.datum_pruefung"
                label="Datum der Prüfung"
                type="date"
                variant="outlined"
                density="comfortable"
                @blur="updateHeader"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="selectedHeader.buchungsschluessel"
                label="Buchungsschlüssel"
                variant="outlined"
                density="comfortable"
                @blur="updateHeader"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedHeader.status"
                :items="statusOptions"
                label="Status"
                variant="outlined"
                density="comfortable"
                @update:model-value="updateHeader"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Positionen-Tabelle -->
      <v-card class="positionen-card">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-view-list</v-icon>
          Positionen
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            size="small"
            prepend-icon="mdi-plus"
            @click="openAddPositionDialog"
          >
            Position hinzufügen
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-data-table
            :headers="positionTableHeaders"
            :items="selectedHeader.positionen || []"
            :loading="loading.positionen"
            item-value="id"
            class="elevation-0 positionen-table"
          >
            <template v-slot:[`item.position`]="{ item }">
              {{ item.position || '—' }}
            </template>
            <template v-slot:[`item.artikel`]="{ item }">
              <div class="d-flex align-center gap-1">
                <strong>{{ item.artikel || '—' }}</strong>
                <v-btn
                  v-if="item.artikel"
                  icon="mdi-open-in-new"
                  size="x-small"
                  color="primary"
                  variant="text"
                  @click="searchGoogleArtikelFromTable(item.artikel)"
                  title="Artikel bei Google suchen"
                  class="ml-1"
                ></v-btn>
              </div>
            </template>
            <template v-slot:[`item.produktmarke`]="{ item }">
              {{ item.produktmarke || '—' }}
            </template>
            <template v-slot:[`item.produktgruppe`]="{ item }">
              {{ item.produktgruppe || '—' }}
            </template>
            <template v-slot:[`item.kaufdatum`]="{ item }">
              <div class="d-flex align-center gap-1">
                <span>{{ item.kaufdatum || '—' }}</span>
                <v-btn
                  v-if="item.kaufdatum"
                  icon="mdi-open-in-new"
                  size="x-small"
                  color="primary"
                  variant="text"
                  @click="searchGoogleArtikelFromTable(item.kaufdatum)"
                  title="Artikelnummer bei Google suchen"
                  class="ml-1"
                ></v-btn>
              </div>
            </template>
            <template v-slot:[`item.herstell_code`]="{ item }">
              <v-text-field
                :model-value="item.herstell_code || ''"
                variant="plain"
                density="compact"
                hide-details
                @blur="updateHerstellcode(item.id, $event.target.value)"
                @keyup.enter="updateHerstellcode(item.id, $event.target.value)"
                placeholder="Klick zum Bearbeiten"
                class="herstell-code-field"
              ></v-text-field>
            </template>
            <template v-slot:[`item.iris_codes`]="{ item }">
              <v-tooltip :text="item.symptom ? `Symptom: ${item.symptom}` : 'Kein Symptom ausgewählt'" location="top">
                <template v-slot:activator="{ props }">
                  <div class="iris-codes-cell" v-bind="props">
                    <v-chip
                      v-if="item.iris_code_1"
                      size="x-small"
                      class="ma-1"
                      color="blue"
                    >
                      {{ item.iris_code_1 }}
                    </v-chip>
                    <v-chip
                      v-if="item.iris_code_2"
                      size="x-small"
                      class="ma-1"
                      color="blue"
                    >
                      {{ item.iris_code_2 }}
                    </v-chip>
                    <v-chip
                      v-if="item.iris_code_3"
                      size="x-small"
                      class="ma-1"
                      color="blue"
                    >
                      {{ item.iris_code_3 }}
                    </v-chip>
                    <v-chip
                      v-if="item.iris_code_4"
                      size="x-small"
                      class="ma-1"
                      color="blue"
                    >
                      {{ item.iris_code_4 }}
                    </v-chip>
                    <span v-if="!item.iris_code_1 && !item.iris_code_2 && !item.iris_code_3 && !item.iris_code_4">
                      —
                    </span>
                  </div>
                </template>
              </v-tooltip>
            </template>
            <template v-slot:[`item.fr`]="{ item }">
              <v-icon v-if="item.fr" color="info" size="small">mdi-check</v-icon>
              <span v-else>—</span>
            </template>
            <template v-slot:[`item.s_kennzeichen`]="{ item }">
              <v-chip
                :color="getSKennzeichenColor(item.s_kennzeichen)"
                size="small"
                variant="flat"
              >
                {{ item.s_kennzeichen || '—' }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="editPosition(item)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="confirmDeletePosition(item.id)"
              ></v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <!-- ========================================
         DIALOG: Neuer Header
         ======================================== -->
    <v-dialog v-model="newHeaderDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          Neuer Lieferschein
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newHeaderData.kunde"
                label="Kunde"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newHeaderData.lieferschein_nr"
                label="Lieferschein-Nr. *"
                variant="outlined"
                density="comfortable"
                :rules="[v => !!v || 'Lieferschein-Nr. ist erforderlich']"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newHeaderData.eingangart_datum"
                label="Eingangart/Datum (z.B. SPEDI 11.12.2025)"
                variant="outlined"
                density="comfortable"
                :hint="`Standard: SPEDI ${formatDateShort(new Date())}`"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newHeaderData.pruefer_name"
                label="Prüfer (Name)"
                variant="outlined"
                density="comfortable"
                readonly
                prepend-inner-icon="mdi-account"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newHeaderData.pruefer_mitarbeiternummer"
                label="Mitarbeiternummer"
                variant="outlined"
                density="comfortable"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newHeaderData.datum_pruefung"
                label="Datum der Prüfung"
                type="date"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newHeaderData.buchungsschluessel"
                label="Buchungsschlüssel"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="newHeaderDialog = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            :loading="loading.saving"
            @click="createNewHeader"
            :disabled="!newHeaderData.lieferschein_nr"
          >
            Erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========================================
         DIALOG: Position hinzufügen/bearbeiten
         ======================================== -->
    <v-dialog v-model="positionDialog" max-width="1000px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2">{{ editingPosition ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ editingPosition ? 'Position bearbeiten' : 'Position hinzufügen' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="positionData.position"
                label="Pos."
                type="number"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="positionData.stueckzahl"
                label="Stückzahl"
                type="number"
                variant="outlined"
                density="comfortable"
                :min="1"
                :max="99"
                hint="Anzahl identischer Positionen"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <div class="d-flex align-center gap-1">
                <v-autocomplete
                  v-model="positionData.artikel"
                  :items="artikelSuggestions"
                  :loading="loading.artikelSearch"
                  label="Artikel"
                  variant="outlined"
                  density="comfortable"
                  item-title="artikel"
                  item-value="artikel"
                  return-object
                  @update:search="searchArtikel"
                  @update:model-value="onArtikelSelected"
                  clearable
                  class="flex-grow-1"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item
                      v-bind="props"
                      :title="item.raw.artikel"
                      :subtitle="item.raw.beschreibung || ''"
                    >
                    </v-list-item>
                  </template>
                  <template v-slot:append-item v-if="artikelSearchTerm && !artikelSuggestions.some(a => a.artikel === artikelSearchTerm)">
                    <v-divider></v-divider>
                    <v-list-item @click="openSaveArtikelDialog">
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-plus-circle</v-icon>
                      </template>
                      <v-list-item-title class="text-primary">
                        Artikel "{{ artikelSearchTerm }}" zum Katalog hinzufügen
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-autocomplete>
                <v-btn
                  v-if="positionData.artikel || artikelSearchTerm"
                  icon="mdi-open-in-new"
                  size="x-small"
                  color="primary"
                  variant="text"
                  @click="searchGoogleArtikelFromDialog"
                  title="Artikel bei Google suchen"
                ></v-btn>
                <v-btn
                  v-if="ENABLE_EAN_SCANNER"
                  icon="mdi-barcode-scan"
                  size="x-small"
                  color="success"
                  variant="text"
                  @click="openEanScanner(false)"
                  title="EAN-Code einzeln scannen"
                  class="ml-1"
                ></v-btn>
                <v-btn
                  v-if="ENABLE_EAN_SCANNER"
                  icon="mdi-barcode-scan"
                  size="x-small"
                  color="orange"
                  variant="text"
                  @click="openEanScanner(true)"
                  title="Batch-Scanner: Multiple Codes nacheinander scannen und automatisch speichern"
                  class="ml-1"
                ></v-btn>
              </div>
              <!-- Artikel-Informationen direkt unter Artikel-Feld -->
              <div style="margin-top: -8px;" v-if="positionData.artikel && selectedArticleInfo">
                <div class="d-flex align-center flex-wrap gap-1">
                  <v-chip
                    v-if="selectedArticleInfo.fr_geeignet"
                    color="success"
                    size="x-small"
                    prepend-icon="mdi-flag"
                  >
                    FR-geeignet
                  </v-chip>
                  <v-chip
                    v-if="selectedArticleInfo.typisch_schrott"
                    color="error"
                    size="x-small"
                    prepend-icon="mdi-delete"
                  >
                    Typisch Schrott
                  </v-chip>
                  <v-chip
                    v-if="selectedArticleInfo.intern_verarbeitung"
                    color="warning"
                    size="x-small"
                    prepend-icon="mdi-cog"
                  >
                    Intern verarbeitet
                  </v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="d-flex align-center gap-1">
                <v-autocomplete
                  v-model="positionData.kaufdatum"
                  :items="artikelnummerSuggestions"
                  :loading="loading.artikelnummerSearch"
                  label="Artikelnummer"
                  variant="outlined"
                  density="comfortable"
                  item-title="artikelnummer"
                  item-value="artikelnummer"
                  return-object
                  @update:search="searchArtikelnummer"
                  @update:model-value="onArtikelnummerSelected"
                  clearable
                  hint="Suche nach Artikelnummer"
                  persistent-hint
                  class="flex-grow-1"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item
                      v-bind="props"
                      :title="item.raw.artikelnummer"
                      :subtitle="item.raw.artikel ? `Artikel: ${item.raw.artikel}` : ''"
                    >
                    </v-list-item>
                  </template>
                </v-autocomplete>
                <v-btn
                  v-if="positionData.kaufdatum || artikelnummerSearchTerm"
                  icon="mdi-open-in-new"
                  size="x-small"
                  color="primary"
                  variant="text"
                  @click="searchGoogleArtikelnummerFromDialog"
                  title="Artikelnummer bei Google suchen"
                ></v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="positionData.produktmarke_id"
                :items="produktmarken"
                label="Produktmarke"
                item-title="name"
                item-value="id"
                variant="outlined"
                density="comfortable"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="positionData.produktgruppe_id"
                :items="produktgruppen"
                label="Produktgruppe"
                item-title="name"
                item-value="id"
                variant="outlined"
                density="comfortable"
                clearable
                @update:model-value="onProduktgruppeChanged"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="positionData.herstell_code"
                label="Herstell-Code"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 mb-2">Symptom & IRIS-Codes</div>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="positionData.symptom_id"
                :items="symptome"
                :disabled="!positionData.produktgruppe_id"
                label="Symptom / Merkmal"
                item-title="bezeichnung"
                item-value="id"
                variant="outlined"
                density="comfortable"
                clearable
                @update:model-value="onSymptomSelected"
                hint="Wählen Sie zuerst eine Produktgruppe"
                persistent-hint
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.bezeichnung }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.raw.iris_code_1">
                      IRIS: {{ item.raw.iris_code_1 }} {{ item.raw.iris_code_2 }} {{ item.raw.iris_code_3 }} {{ item.raw.iris_code_4 }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="positionData.iris_code_1"
                :items="irisCodes"
                :loading="loading.irisSearch"
                label="IRIS-Code 1"
                variant="outlined"
                density="comfortable"
                item-title="iris_code"
                item-value="iris_code"
                clearable
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.iris_code }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.raw.beschreibung">
                      {{ item.raw.beschreibung }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="positionData.iris_code_2"
                :items="irisCodes"
                :loading="loading.irisSearch"
                label="IRIS-Code 2"
                variant="outlined"
                density="comfortable"
                item-title="iris_code"
                item-value="iris_code"
                clearable
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.iris_code }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.raw.beschreibung">
                      {{ item.raw.beschreibung }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="positionData.iris_code_3"
                :items="irisCodes"
                :loading="loading.irisSearch"
                label="IRIS-Code 3"
                variant="outlined"
                density="comfortable"
                item-title="iris_code"
                item-value="iris_code"
                clearable
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.iris_code }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.raw.beschreibung">
                      {{ item.raw.beschreibung }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="positionData.iris_code_4"
                :items="irisCodes"
                :loading="loading.irisSearch"
                label="IRIS-Code 4"
                variant="outlined"
                density="comfortable"
                item-title="iris_code"
                item-value="iris_code"
                clearable
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.iris_code }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.raw.beschreibung">
                      {{ item.raw.beschreibung }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="positionData.auftrags_nr_sepas"
                label="Auftrags-Nr. Sepas"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-checkbox
                v-model="positionData.fr"
                label="FR/Interne Verarbeitung?"
                color="primary"
                hint="Kein S-Kennzeichen"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="positionData.s_kennzeichen"
                :items="['CO', 'KO']"
                label="S-Kennzeichen"
                variant="outlined"
                density="comfortable"
                :disabled="positionData.fr"
                hint="Automatisch KO bei Y7-Codes"
                persistent-hint
              ></v-select>
            </v-col>
          </v-row>

          <v-alert
            v-if="positionData.fr"
            type="info"
            density="compact"
            class="mt-4"
          >
            <strong>FR aktiv:</strong> Diese Position wird nach Frankreich geliefert - S-Kennzeichen wird nicht gesetzt.
          </v-alert>
          <v-alert
            v-if="showY7Warning"
            type="warning"
            density="compact"
            class="mt-4"
          >
            <strong>Automatische Anpassung:</strong> Da IRIS-Code 4 "Y/" oder "Y7" enthält, wird das S-Kennzeichen automatisch auf "KO" gesetzt.
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closePositionDialog">Abbrechen</v-btn>
          <v-btn
            color="primary"
            :loading="loading.saving"
            @click="savePosition"
          >
            {{ editingPosition ? 'Speichern' : 'Hinzufügen' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========================================
         DIALOG: Neuen Artikel zum Katalog hinzufügen
         ======================================== -->
    <v-dialog v-model="saveArtikelDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2">mdi-package-variant-plus</v-icon>
          Artikel zum Katalog hinzufügen
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-alert type="info" density="compact" class="mb-4">
            Dieser Artikel ist noch nicht im Katalog. Sie können ihn jetzt hinzufügen, damit er in Zukunft direkt verfügbar ist.
          </v-alert>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="newArtikelData.artikel"
                label="Artikel"
                variant="outlined"
                density="comfortable"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="newArtikelData.artikelnummer"
                label="Artikelnummer"
                variant="outlined"
                density="comfortable"
                hint="Offizielle Artikelnummer"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newArtikelData.beschreibung"
                label="Beschreibung (optional)"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newArtikelData.ean"
                label="EAN-Code (optional)"
                variant="outlined"
                density="comfortable"
                hint="Barcode/EAN für Scanner-Funktionalität"
                persistent-hint
                maxlength="20"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                v-model="newArtikelData.produktmarke_id"
                :items="produktmarken"
                label="Produktmarke (optional)"
                item-title="name"
                item-value="id"
                variant="outlined"
                density="comfortable"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                v-model="newArtikelData.produktgruppe_id"
                :items="produktgruppen"
                label="Produktgruppe (optional)"
                item-title="name"
                item-value="id"
                variant="outlined"
                density="comfortable"
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 mb-2">Standard IRIS-Codes (optional)</div>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="newArtikelData.iris_code_1"
                label="IRIS-Code 1"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="newArtikelData.iris_code_2"
                label="IRIS-Code 2"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="newArtikelData.iris_code_3"
                label="IRIS-Code 3"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="newArtikelData.iris_code_4"
                label="IRIS-Code 4"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 mb-3">Artikel-Kategorisierung</div>
          <v-row>
            <v-col cols="4">
              <v-checkbox
                v-model="newArtikelData.fr_geeignet"
                label="FR-geeignet"
                color="success"
                hint="Artikel kann nach Frankreich"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="4">
              <v-checkbox
                v-model="newArtikelData.typisch_schrott"
                label="Typisch Schrott"
                color="error"
                hint="Landet meist im Schrott"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="4">
              <v-checkbox
                v-model="newArtikelData.intern_verarbeitung"
                label="Intern verarbeitet"
                color="warning"
                hint="Wird intern verwendet"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="saveArtikelDialog = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            :loading="loading.saving"
            @click="saveArtikelToKatalog"
          >
            Zum Katalog hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========================================
         DIALOG: Header löschen
         ======================================== -->
    <v-dialog v-model="deleteHeaderDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon class="mr-2">mdi-delete-alert</v-icon>
          Lieferschein löschen?
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          Möchten Sie diesen Lieferschein wirklich löschen? Alle zugehörigen Positionen werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteHeaderDialog = false">Abbrechen</v-btn>
          <v-btn
            color="error"
            :loading="loading.deleting"
            @click="deleteHeader"
          >
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========================================
         DIALOG: Position löschen
         ======================================== -->
    <v-dialog v-model="deletePositionDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon class="mr-2">mdi-delete-alert</v-icon>
          Position löschen?
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          Möchten Sie diese Position wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deletePositionDialog = false">Abbrechen</v-btn>
          <v-btn
            color="error"
            :loading="loading.deleting"
            @click="deletePosition"
          >
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========================================
         DIALOG: EAN-Scanner (Feature-Flag kontrolliert)
         ======================================== -->
    <v-dialog v-model="showEanScanner" max-width="600px" persistent v-if="ENABLE_EAN_SCANNER">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon :color="batchScanMode ? 'orange' : 'success'" size="24" class="mr-2">mdi-barcode-scan</v-icon>
          {{ batchScanMode ? 'Batch-Scanner' : 'EAN-Scanner' }}
          <v-chip
            v-if="batchScanMode && scanCount > 0"
            color="orange"
            size="small"
            class="ml-2"
          >
            {{ scanCount }} gescannt
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="closeEanScanner"
          ></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <!-- Scanner-Container -->
          <div v-if="!scannerReady && !scannerError" class="text-center py-8">
            <v-progress-circular indeterminate color="success" size="64"></v-progress-circular>
            <div class="mt-4">Kamera wird initialisiert...</div>
          </div>

          <div v-if="scannerError" class="text-center py-8">
            <v-icon color="error" size="64" class="mb-4">mdi-camera-off</v-icon>
            <div class="text-error mb-4">{{ scannerError }}</div>
            <v-btn color="primary" @click="openEanScanner">Erneut versuchen</v-btn>
          </div>

          <div v-if="scannerReady" class="scanner-container">
            <div id="ean-scanner" class="scanner-viewport"></div>
            <div class="scanner-overlay">
              <div class="scanner-frame"></div>
            </div>
          </div>

          <!-- Batch-Scan Übersicht -->
          <div v-if="batchScanMode && scannedItems.length > 0" class="mt-4">
            <v-divider class="mb-3"></v-divider>
            <h4 class="text-h6 mb-2">Gescannte Artikel ({{ scanCount }})</h4>
            <div class="scan-items-list" style="max-height: 150px; overflow-y: auto;">
              <v-chip
                v-for="(item, index) in scannedItems.slice(-5)"
                :key="index"
                size="small"
                color="success"
                class="ma-1"
              >
                <v-icon start size="small">mdi-check-circle</v-icon>
                {{ item.artikel }} ({{ item.timestamp }})
              </v-chip>
              <div v-if="scannedItems.length > 5" class="text-caption text-center mt-2">
                ...und {{ scannedItems.length - 5 }} weitere
              </div>
            </div>
          </div>

          <!-- Manuelle Eingabe -->
          <div class="mt-4">
            <v-text-field
              v-model="scannedEan"
              :label="batchScanMode ? 'EAN-Code für Batch-Scan' : 'EAN-Code (manuelle Eingabe)'"
              variant="outlined"
              density="comfortable"
              @keyup.enter="processScannedEan"
              :hint="batchScanMode ? 'Gescannte Artikel werden automatisch mit Menge 1 gespeichert' : 'Gebe die EAN-Nummer manuell ein oder halte einen Barcode vor die Kamera'"
              persistent-hint
            >
              <template v-slot:append>
                <v-btn
                  :disabled="!scannedEan"
                  color="success"
                  @click="processScannedEan"
                  size="small"
                >
                  Suchen
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeEanScanner">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { useRoute } from "vue-router";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";
import Quagga from '@ericblade/quagga2';
import { jsPDF } from 'jspdf';

// ======== Feature Flags ========
const ENABLE_EAN_SCANNER = true; // Scanner ist jetzt aktiviert für Tests

// ======== Setup ========
const toast = useToast();
const authStore = useAuthStore();
const route = useRoute();

// ======== Zustandsverwaltung ========
const headers = ref([]);
const selectedHeader = ref(null);
const statusFilter = ref("alle");

const loading = ref({
  headers: false,
  positionen: false,
  saving: false,
  deleting: false,
  artikelSearch: false,
  artikelnummerSearch: false,
  irisSearch: false,
});

// Dialog-Zustände
const newHeaderDialog = ref(false);
const positionDialog = ref(false);
const deleteHeaderDialog = ref(false);
const deletePositionDialog = ref(false);
const saveArtikelDialog = ref(false);

// Daten für neue/bearbeitete Einträge
const newHeaderData = ref({
  kunde: "",
  lieferschein_nr: "",
  eingangart_datum: `SPEDI ${formatDateShort(new Date())}`,
  pruefer_name: "",
  pruefer_mitarbeiternummer: "",
  datum_pruefung: new Date().toISOString().split('T')[0],
  buchungsschluessel: "D51",
});

const positionData = ref({
  position: null,
  stueckzahl: 1,
  artikel: "",
  herstell_code: "",
  kaufdatum: "",
  produktmarke_id: null,
  produktgruppe_id: null,
  symptom_id: null,
  iris_code_1: "",
  iris_code_2: "",
  iris_code_3: "",
  iris_code_4: "",
  auftrags_nr_sepas: "",
  s_kennzeichen: "CO",
  fr: false,
  reihenfolge: 0,
});

const selectedArticleInfo = ref(null);

const editingPosition = ref(null);
const deleteHeaderId = ref(null);
const deletePositionId = ref(null);

// Katalog-Daten
const artikelSuggestions = ref([]);
const artikelnummerSuggestions = ref([]);
const irisCodes = ref([]);
const artikelSearchTerm = ref("");
const artikelnummerSearchTerm = ref("");
const produktmarken = ref([]);
const produktgruppen = ref([]);
const symptome = ref([]);

const newArtikelData = ref({
  artikel: "",
  artikelnummer: "",
  beschreibung: "",
  ean: "", // EAN-Code für zukünftige Scanner-Funktionalität
  produktmarke_id: null,
  produktgruppe_id: null,
  iris_code_1: "",
  iris_code_2: "",
  iris_code_3: "",
  iris_code_4: "",
  fr_geeignet: false,
  typisch_schrott: false,
  intern_verarbeitung: false,
});

// ======== Computed Properties ========
const filteredHeaders = computed(() => {
  if (statusFilter.value === "alle") {
    return headers.value;
  }
  return headers.value.filter(h => h.status === statusFilter.value);
});

const showY7Warning = computed(() => {
  return !positionData.value.fr &&
         positionData.value.iris_code_4 &&
         (positionData.value.iris_code_4.includes('Y/') || positionData.value.iris_code_4.includes('Y7'));
});

const headerTableHeaders = [
  { title: "Lieferschein-Nr.", key: "lieferschein_nr", sortable: true },
  { title: "Kunde", key: "kunde", sortable: true },
  { title: "Prüfungsdatum", key: "datum_pruefung", sortable: true },
  { title: "Positionen", key: "anzahl_positionen", sortable: true },
  { title: "Status", key: "status", sortable: true },
  { title: "Aktionen", key: "actions", sortable: false, align: "center" },
];

const positionTableHeaders = [
  { title: "Pos.", key: "position", sortable: true, width: "80px" },
  { title: "Artikel", key: "artikel", sortable: true },
  { title: "Marke", key: "produktmarke", sortable: true, width: "100px" },
  { title: "Gruppe", key: "produktgruppe", sortable: true, width: "120px" },
  { title: "Artikelnummer", key: "kaufdatum", sortable: true },
  { title: "Herstell-Code", key: "herstell_code", sortable: true },
  { title: "IRIS-Codes", key: "iris_codes", sortable: false },
  { title: "FR", key: "fr", sortable: true, width: "60px" },
  { title: "S", key: "s_kennzeichen", sortable: true, width: "80px" },
  { title: "Aktionen", key: "actions", sortable: false, align: "center", width: "120px" },
];

const statusOptions = [
  { title: "Offen", value: "offen" },
  { title: "In Bearbeitung", value: "in_bearbeitung" },
  { title: "Abgeschlossen", value: "abgeschlossen" },
];

// Validierung für Prod-Code: 4 Ziffern oder leer
const prodCodeRules = [
  (v) => !v || /^\d{4}$/.test(v) || 'Prod.-Code muss 4 Ziffern sein oder leer bleiben'
];

// ======== Hilfsfunktionen ========
function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (e) {
    return dateString;
  }
}

function formatDateShort(date) {
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getStatusColor(status) {
  switch (status) {
    case "offen":
      return "warning";
    case "in_bearbeitung":
      return "info";
    case "abgeschlossen":
      return "success";
    default:
      return "grey";
  }
}

function getStatusText(status) {
  switch (status) {
    case "offen":
      return "Offen";
    case "in_bearbeitung":
      return "In Bearbeitung";
    case "abgeschlossen":
      return "Abgeschlossen";
    default:
      return status;
  }
}

function getSKennzeichenColor(s) {
  if (s === "CO") return "success";
  if (s === "KO") return "error";
  return "grey";
}

// ======== API-Funktionen: Headers ========
async function fetchHeaders() {
  loading.value.headers = true;
  try {
    const response = await axios.get(`${API_BASE_URL}retouren_v2/index.php?action=get_headers`, {
      withCredentials: true,
    });

    if (response.data.status === "success") {
      headers.value = response.data.headers;
    } else {
      toast.error(response.data.message || "Fehler beim Laden der Lieferscheine");
    }
  } catch (error) {
    console.error("Fehler beim Laden der Headers:", error);
    toast.error("Verbindungsfehler beim Laden der Lieferscheine");
  } finally {
    loading.value.headers = false;
  }
}

async function loadHeader(headerId) {
  loading.value.headers = true;
  try {
    const response = await axios.get(`${API_BASE_URL}retouren_v2/index.php?action=get_header&header_id=${headerId}`, {
      withCredentials: true,
    });

    if (response.data.status === "success") {
      selectedHeader.value = response.data.header;
    } else {
      toast.error(response.data.message || "Fehler beim Laden des Lieferscheins");
    }
  } catch (error) {
    console.error("Fehler beim Laden des Headers:", error);
    toast.error("Verbindungsfehler beim Laden des Lieferscheins");
  } finally {
    loading.value.headers = false;
  }
}

function openNewHeaderDialog() {
  // Prüfer-Daten automatisch ausfüllen
  if (authStore.user) {
    newHeaderData.value.pruefer_name = authStore.user.nachname;
    newHeaderData.value.pruefer_mitarbeiternummer = authStore.user.mitarbeiternummer;
  }
  newHeaderData.value.datum_pruefung = new Date().toISOString().split('T')[0];
  newHeaderData.value.eingangart_datum = `SPEDI ${formatDateShort(new Date())}`;
  newHeaderData.value.buchungsschluessel = "D51";
  newHeaderDialog.value = true;
}

async function createNewHeader() {
  if (!newHeaderData.value.lieferschein_nr) {
    toast.warning("Bitte Lieferschein-Nr. eingeben");
    return;
  }

  loading.value.saving = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}retouren_v2/index.php`,
      {
        action: "add_header",
        ...newHeaderData.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Lieferschein erfolgreich erstellt");
      newHeaderDialog.value = false;
      await fetchHeaders();
      // Direkt in den neuen Header wechseln
      await loadHeader(response.data.header_id);
    } else {
      toast.error(response.data.message || "Fehler beim Erstellen");
    }
  } catch (error) {
    console.error("Fehler beim Erstellen:", error);
    toast.error("Verbindungsfehler beim Erstellen");
  } finally {
    loading.value.saving = false;
  }
}

async function updateHeader() {
  if (!selectedHeader.value) return;

  loading.value.saving = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}retouren_v2/index.php`,
      {
        action: "update_header",
        header_id: selectedHeader.value.id,
        kunde: selectedHeader.value.kunde,
        lieferschein_nr: selectedHeader.value.lieferschein_nr,
        eingangart_datum: selectedHeader.value.eingangart_datum,
        pruefer_name: selectedHeader.value.pruefer_name,
        pruefer_mitarbeiternummer: selectedHeader.value.pruefer_mitarbeiternummer,
        datum_pruefung: selectedHeader.value.datum_pruefung,
        buchungsschluessel: selectedHeader.value.buchungsschluessel,
        status: selectedHeader.value.status,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Änderungen gespeichert");
      await fetchHeaders();
    } else {
      toast.error(response.data.message || "Fehler beim Speichern");
    }
  } catch (error) {
    console.error("Fehler beim Speichern:", error);
    toast.error("Verbindungsfehler beim Speichern");
  } finally {
    loading.value.saving = false;
  }
}

function confirmDeleteHeader(headerId) {
  deleteHeaderId.value = headerId;
  deleteHeaderDialog.value = true;
}

async function deleteHeader() {
  loading.value.deleting = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}retouren_v2/index.php`,
      {
        action: "delete_header",
        header_id: deleteHeaderId.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Lieferschein gelöscht");
      deleteHeaderDialog.value = false;
      deleteHeaderId.value = null;

      // Wenn aktuell angezeigt, zurück zur Liste
      if (selectedHeader.value && selectedHeader.value.id === deleteHeaderId.value) {
        selectedHeader.value = null;
      }

      await fetchHeaders();
    } else {
      toast.error(response.data.message || "Fehler beim Löschen");
    }
  } catch (error) {
    console.error("Fehler beim Löschen:", error);
    toast.error("Verbindungsfehler beim Löschen");
  } finally {
    loading.value.deleting = false;
  }
}

function backToList() {
  selectedHeader.value = null;
  fetchHeaders();
}

async function exportToPDF(headerId) {
  try {
    loading.value.headers = true;

    // Header mit Positionen laden
    const response = await axios.get(`${API_BASE_URL}retouren_v2/index.php?action=get_header&header_id=${headerId}`, {
      withCredentials: true,
    });

    if (response.data.status !== "success") {
      toast.error("Fehler beim Laden der Daten");
      return;
    }

    const header = response.data.header;

    // PDF erstellen
    const doc = new jsPDF();

    // Titel
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Retouren Lieferschein', 105, 20, { align: 'center' });

    // Trennlinie
    doc.setLineWidth(0.5);
    doc.line(15, 25, 195, 25);

    // Header-Informationen in zwei Spalten
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    let yPos = 35;

    // Linke Spalte
    doc.text('Lieferschein-Informationen:', 15, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 7;
    doc.text(`Lieferschein-Nr.:`, 15, yPos);
    doc.text(header.lieferschein_nr || '—', 60, yPos);
    yPos += 6;
    doc.text(`Kunde:`, 15, yPos);
    doc.text(header.kunde || '—', 60, yPos);
    yPos += 6;
    doc.text(`Eingangsart/Datum:`, 15, yPos);
    doc.text(header.eingangart_datum || '—', 60, yPos);
    yPos += 6;
    doc.text(`Buchungsschlüssel:`, 15, yPos);
    doc.text(header.buchungsschluessel || '—', 60, yPos);

    // Rechte Spalte
    yPos = 42;
    doc.text(`Prüfer:`, 110, yPos);
    doc.text(header.pruefer_name || '—', 145, yPos);
    yPos += 6;
    doc.text(`Mitarbeiter-Nr.:`, 110, yPos);
    doc.text(header.pruefer_mitarbeiternummer || '—', 145, yPos);
    yPos += 6;
    doc.text(`Prüfungsdatum:`, 110, yPos);
    doc.text(formatDate(header.datum_pruefung), 145, yPos);
    yPos += 6;
    doc.text(`Status:`, 110, yPos);
    doc.text(getStatusText(header.status), 145, yPos);

    yPos += 15;

    // Positionen-Tabelle manuell erstellen
    if (header.positionen && header.positionen.length > 0) {
      doc.setFontSize(9);
      const tableStartY = yPos;
      const rowHeight = 8;
      const headerHeight = 10;
      const columnWidths = [12, 30, 22, 25, 22, 22, 35, 10, 10];
      const columnTitles = ['Pos.', 'Artikel', 'Marke', 'Gruppe', 'Art.Nr.', 'Herstell-Code', 'IRIS-Codes', 'S', 'FR'];
      let currentX = 15;
      let currentY = tableStartY;

      // Header-Zeile
      doc.setFillColor(41, 128, 185);
      doc.setTextColor(255);
      doc.setFont(undefined, 'bold');

      currentX = 15;
      for (let i = 0; i < columnTitles.length; i++) {
        doc.rect(currentX, currentY, columnWidths[i], headerHeight, 'F');
        doc.rect(currentX, currentY, columnWidths[i], headerHeight, 'S');
        doc.text(columnTitles[i], currentX + columnWidths[i]/2, currentY + 7, { align: 'center' });
        currentX += columnWidths[i];
      }
      currentY += headerHeight;

      // Daten-Zeilen
      doc.setFillColor(255, 255, 255);
      doc.setTextColor(0);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);

      header.positionen.forEach(pos => {
        const irisCodes = [pos.iris_code_1, pos.iris_code_2, pos.iris_code_3, pos.iris_code_4]
          .filter(c => c)
          .join(' ');

        const rowData = [
          String(pos.position || '—'),
          String(pos.artikel || '—'),
          String(pos.produktmarke || '—'),
          String(pos.produktgruppe || '—'),
          String(pos.kaufdatum || '—'),
          String(pos.herstell_code || '—'),
          String(irisCodes || '—'),
          String(pos.s_kennzeichen || '—'),
          pos.fr ? 'Ja' : 'Nein'
        ];

        currentX = 15;
        for (let i = 0; i < rowData.length; i++) {
          doc.rect(currentX, currentY, columnWidths[i], rowHeight, 'S');
          const text = String(rowData[i]);
          if (text.length > 25) {
            // Langer Text - kleinere Schrift
            doc.setFontSize(7);
            const lines = doc.splitTextToSize(text, columnWidths[i] - 2);
            doc.text(String(lines[0] || ''), currentX + 1, currentY + 6);
            doc.setFontSize(8);
          } else {
            doc.text(text, currentX + columnWidths[i]/2, currentY + 6, { align: 'center' });
          }
          currentX += columnWidths[i];
        }
        currentY += rowHeight;

        // Seitenumbruch bei Bedarf
        if (currentY > 250) {
          doc.addPage();
          currentY = 20;
        }
      });

      // Fußzeile mit Anzahl Positionen
      currentY += 10;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text(`Gesamt: ${header.positionen.length} Position(en)`, 15, currentY);
    } else {
      doc.setFontSize(10);
      doc.setFont(undefined, 'italic');
      doc.text('Keine Positionen vorhanden', 15, yPos);
    }

    // Fußzeile mit Datum
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Erstellt am: ${new Date().toLocaleDateString('de-DE')} ${new Date().toLocaleTimeString('de-DE')}`,
        15,
        doc.internal.pageSize.height - 10
      );
      doc.text(
        `Seite ${i} von ${pageCount}`,
        doc.internal.pageSize.width - 15,
        doc.internal.pageSize.height - 10,
        { align: 'right' }
      );
    }

    // PDF speichern mit erweitertem Dateinamen
    const mitarbeiterNr = header.pruefer_mitarbeiternummer || 'unbekannt';
    const fileName = `Lieferschein_${header.lieferschein_nr || headerId}_${new Date().toISOString().split('T')[0]}_Mitarbeiter${mitarbeiterNr}.pdf`;
    doc.save(fileName);

    toast.success('PDF wurde erstellt');
  } catch (error) {
    console.error('Fehler beim PDF-Export:', error);
    toast.error('Fehler beim Erstellen der PDF');
  } finally {
    loading.value.headers = false;
  }
}

// ======== API-Funktionen: Positionen ========
function openAddPositionDialog() {
  editingPosition.value = null;

  // Automatische Position-Nummer: Finde höchste Position und +1
  let nextPosition = 1;
  if (selectedHeader.value?.positionen && selectedHeader.value.positionen.length > 0) {
    const positions = selectedHeader.value.positionen
      .map(p => p.position)
      .filter(p => p != null && !isNaN(p));
    if (positions.length > 0) {
      nextPosition = Math.max(...positions) + 1;
    }
  }

  positionData.value = {
    position: nextPosition,
    stueckzahl: 1,
    artikel: "",
    herstell_code: "",
    kaufdatum: "",
    produktmarke_id: null,
    produktgruppe_id: null,
    symptom_id: null,
    iris_code_1: "",
    iris_code_2: "",
    iris_code_3: "",
    iris_code_4: "",
    auftrags_nr_sepas: "",
    s_kennzeichen: "CO",
    fr: false,
    reihenfolge: 0,
  };
  selectedArticleInfo.value = null;
  positionDialog.value = true;
}

function onArtikelSelected(selectedArtikel) {
  if (!selectedArtikel) return;

  // Setze den Artikelnamen
  positionData.value.artikel = selectedArtikel.artikel;

  // Lade Artikel-Informationen (FR-Eignung, Schrott-Tendenz, etc.)
  selectedArticleInfo.value = {
    fr_geeignet: selectedArtikel.fr_geeignet || false,
    typisch_schrott: selectedArtikel.typisch_schrott || false,
    intern_verarbeitung: selectedArtikel.intern_verarbeitung || false,
  };

  // Fülle Produktmarke & Produktgruppe automatisch aus
  if (selectedArtikel.produktmarke_id) {
    positionData.value.produktmarke_id = selectedArtikel.produktmarke_id;
  }
  if (selectedArtikel.produktgruppe_id) {
    positionData.value.produktgruppe_id = selectedArtikel.produktgruppe_id;
    // Symptome für die Produktgruppe laden
    onProduktgruppeChanged(selectedArtikel.produktgruppe_id);
  }

  // Fülle Artikelnummer automatisch aus (artikelnummer vom Backend, speichern in kaufdatum)
  if (selectedArtikel.artikelnummer) {
    positionData.value.kaufdatum = selectedArtikel.artikelnummer;
  } else if (selectedArtikel.kaufdatum) {
    positionData.value.kaufdatum = selectedArtikel.kaufdatum;
  }

  // Fülle IRIS-Codes automatisch aus wenn vorhanden
  if (selectedArtikel.iris_code_1) {
    positionData.value.iris_code_1 = selectedArtikel.iris_code_1;
  }
  if (selectedArtikel.iris_code_2) {
    positionData.value.iris_code_2 = selectedArtikel.iris_code_2;
  }
  if (selectedArtikel.iris_code_3) {
    positionData.value.iris_code_3 = selectedArtikel.iris_code_3;
  }
  if (selectedArtikel.iris_code_4) {
    positionData.value.iris_code_4 = selectedArtikel.iris_code_4;
  }
}

function editPosition(position) {
  editingPosition.value = position;
  positionData.value = { ...position };
  selectedArticleInfo.value = null;
  // Symptome laden wenn Produktgruppe gesetzt ist
  if (position.produktgruppe_id) {
    onProduktgruppeChanged(position.produktgruppe_id);
  }
  positionDialog.value = true;
}

async function savePosition() {
  if (!selectedHeader.value) return;

  loading.value.saving = true;
  try {
    const stueckzahl = positionData.value.stueckzahl || 1;

    // Mehrere Positionen erstellen wenn Stückzahl > 1
    if (!editingPosition.value && stueckzahl > 1) {
      // Mehrere identische Positionen erstellen
      for (let i = 0; i < stueckzahl; i++) {
        const payload = {
          action: "add_position",
          header_id: selectedHeader.value.id,
          position: (positionData.value.position || 1) + i,
          artikel: positionData.value.artikel,
          herstell_code: positionData.value.herstell_code,
          kaufdatum: positionData.value.kaufdatum,
          produktmarke_id: positionData.value.produktmarke_id,
          produktgruppe_id: positionData.value.produktgruppe_id,
          symptom_id: positionData.value.symptom_id,
          iris_code_1: positionData.value.iris_code_1,
          iris_code_2: positionData.value.iris_code_2,
          iris_code_3: positionData.value.iris_code_3,
          iris_code_4: positionData.value.iris_code_4,
          auftrags_nr_sepas: positionData.value.auftrags_nr_sepas,
          s_kennzeichen: positionData.value.s_kennzeichen,
          fr: positionData.value.fr,
          reihenfolge: positionData.value.reihenfolge,
        };

        const response = await axios.post(
          `${API_BASE_URL}retouren_v2/index.php`,
          payload,
          { withCredentials: true }
        );

        if (response.data.status !== "success") {
          throw new Error(response.data.message || "Fehler beim Speichern");
        }
      }
      toast.success(`${stueckzahl} Positionen hinzugefügt`);
    } else {
      // Einzelne Position erstellen/bearbeiten
      const action = editingPosition.value ? "update_position" : "add_position";
      const payload = {
        action,
        header_id: selectedHeader.value.id,
        artikel: positionData.value.artikel,
        herstell_code: positionData.value.herstell_code,
        kaufdatum: positionData.value.kaufdatum,
        produktmarke_id: positionData.value.produktmarke_id,
        produktgruppe_id: positionData.value.produktgruppe_id,
        symptom_id: positionData.value.symptom_id,
        iris_code_1: positionData.value.iris_code_1,
        iris_code_2: positionData.value.iris_code_2,
        iris_code_3: positionData.value.iris_code_3,
        iris_code_4: positionData.value.iris_code_4,
        auftrags_nr_sepas: positionData.value.auftrags_nr_sepas,
        s_kennzeichen: positionData.value.s_kennzeichen,
        fr: positionData.value.fr,
        reihenfolge: positionData.value.reihenfolge,
        position: positionData.value.position,
      };

      if (editingPosition.value) {
        payload.position_id = editingPosition.value.id;
      }

      const response = await axios.post(
        `${API_BASE_URL}retouren_v2/index.php`,
        payload,
        { withCredentials: true }
      );

      if (response.data.status === "success") {
        toast.success(editingPosition.value ? "Position aktualisiert" : "Position hinzugefügt");
      } else {
        toast.error(response.data.message || "Fehler beim Speichern");
        return;
      }
    }

    positionDialog.value = false;
    // Header neu laden um Positionen zu aktualisieren
    await loadHeader(selectedHeader.value.id);
  } catch (error) {
    console.error("Fehler beim Speichern:", error);
    toast.error("Verbindungsfehler beim Speichern");
  } finally {
    loading.value.saving = false;
  }
}

function confirmDeletePosition(positionId) {
  deletePositionId.value = positionId;
  deletePositionDialog.value = true;
}

async function deletePosition() {
  loading.value.deleting = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}retouren_v2/index.php`,
      {
        action: "delete_position",
        position_id: deletePositionId.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Position gelöscht");
      deletePositionDialog.value = false;
      deletePositionId.value = null;
      // Header neu laden
      await loadHeader(selectedHeader.value.id);
    } else {
      toast.error(response.data.message || "Fehler beim Löschen");
    }
  } catch (error) {
    console.error("Fehler beim Löschen:", error);
    toast.error("Verbindungsfehler beim Löschen");
  } finally {
    loading.value.deleting = false;
  }
}

function closePositionDialog() {
  positionDialog.value = false;
  editingPosition.value = null;
}

// ======== API-Funktionen: Kataloge ========
let artikelSearchTimeout = null;
async function searchArtikel(query) {
  artikelSearchTerm.value = query || "";

  if (!query || query.length < 2) {
    artikelSuggestions.value = [];
    return;
  }

  // Debounce
  clearTimeout(artikelSearchTimeout);
  artikelSearchTimeout = setTimeout(async () => {
    loading.value.artikelSearch = true;
    try {
      const response = await axios.get(
        `${API_BASE_URL}retouren_v2/index.php?action=search_artikel&query=${encodeURIComponent(query)}`,
        { withCredentials: true }
      );

      if (response.data.status === "success") {
        // Duplikate entfernen (basierend auf artikel-Name)
        const seen = new Map();
        response.data.artikel.forEach(item => {
          if (!seen.has(item.artikel)) {
            seen.set(item.artikel, item);
          }
        });
        artikelSuggestions.value = Array.from(seen.values());
      }
    } catch (error) {
      console.error("Fehler bei Artikelsuche:", error);
    } finally {
      loading.value.artikelSearch = false;
    }
  }, 300);
}

let artikelnummerSearchTimeout = null;
async function searchArtikelnummer(query) {
  artikelnummerSearchTerm.value = query || "";

  if (!query || query.length < 2) {
    artikelnummerSuggestions.value = [];
    return;
  }

  // Debounce
  clearTimeout(artikelnummerSearchTimeout);
  artikelnummerSearchTimeout = setTimeout(async () => {
    loading.value.artikelnummerSearch = true;
    try {
      const response = await axios.get(
        `${API_BASE_URL}retouren_v2/index.php?action=search_artikelnummer&query=${encodeURIComponent(query)}`,
        { withCredentials: true }
      );

      if (response.data.status === "success") {
        artikelnummerSuggestions.value = response.data.artikel;
      } else {
        console.error("Artikelnummer-Suche Fehler:", response.data.message);
      }
    } catch (error) {
      console.error("Fehler bei Artikelnummer-Suche:", error);
    } finally {
      loading.value.artikelnummerSearch = false;
    }
  }, 300);
}

function onArtikelnummerSelected(selectedArtikel) {
  if (!selectedArtikel) return;

  // Setze die Artikelnummer
  positionData.value.kaufdatum = selectedArtikel.artikelnummer;

  // Fülle auch den Artikel-Namen aus
  if (selectedArtikel.artikel) {
    positionData.value.artikel = selectedArtikel.artikel;
  }

  // Lade Artikel-Informationen (FR-Eignung, Schrott-Tendenz, etc.)
  selectedArticleInfo.value = {
    fr_geeignet: selectedArtikel.fr_geeignet || false,
    typisch_schrott: selectedArtikel.typisch_schrott || false,
    intern_verarbeitung: selectedArtikel.intern_verarbeitung || false,
  };

  // Fülle Produktmarke & Produktgruppe automatisch aus
  if (selectedArtikel.produktmarke_id) {
    positionData.value.produktmarke_id = selectedArtikel.produktmarke_id;
  }
  if (selectedArtikel.produktgruppe_id) {
    positionData.value.produktgruppe_id = selectedArtikel.produktgruppe_id;
    // Symptome für die Produktgruppe laden
    onProduktgruppeChanged(selectedArtikel.produktgruppe_id);
  }

  // Fülle IRIS-Codes automatisch aus wenn vorhanden
  if (selectedArtikel.iris_code_1) {
    positionData.value.iris_code_1 = selectedArtikel.iris_code_1;
  }
  if (selectedArtikel.iris_code_2) {
    positionData.value.iris_code_2 = selectedArtikel.iris_code_2;
  }
  if (selectedArtikel.iris_code_3) {
    positionData.value.iris_code_3 = selectedArtikel.iris_code_3;
  }
  if (selectedArtikel.iris_code_4) {
    positionData.value.iris_code_4 = selectedArtikel.iris_code_4;
  }
}

function openSaveArtikelDialog() {
  newArtikelData.value = {
    artikel: artikelSearchTerm.value,
    artikelnummer: positionData.value.kaufdatum || "",
    beschreibung: "",
    produktmarke_id: positionData.value.produktmarke_id || null,
    produktgruppe_id: positionData.value.produktgruppe_id || null,
    iris_code_1: positionData.value.iris_code_1 || "",
    iris_code_2: positionData.value.iris_code_2 || "",
    iris_code_3: positionData.value.iris_code_3 || "",
    iris_code_4: positionData.value.iris_code_4 || "",
    fr_geeignet: false,
    typisch_schrott: false,
    intern_verarbeitung: false,
  };
  saveArtikelDialog.value = true;
}

async function saveArtikelToKatalog() {
  if (!newArtikelData.value.artikel) {
    toast.warning("Artikelname ist erforderlich");
    return;
  }

  loading.value.saving = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}retouren_v2/index.php`,
      {
        action: "add_artikel_to_katalog",
        ...newArtikelData.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Artikel wurde zum Katalog hinzugefügt");
      saveArtikelDialog.value = false;

      // Artikel in der aktuellen Position setzen
      positionData.value.artikel = newArtikelData.value.artikel;
      positionData.value.kaufdatum = newArtikelData.value.artikelnummer;

      // Suche erneut durchführen um den neuen Artikel anzuzeigen
      if (artikelSearchTerm.value) {
        await searchArtikel(artikelSearchTerm.value);
      }
    } else {
      toast.error(response.data.message || "Fehler beim Hinzufügen des Artikels");
    }
  } catch (error) {
    console.error("Fehler beim Speichern des Artikels:", error);
    toast.error("Verbindungsfehler beim Speichern");
  } finally {
    loading.value.saving = false;
  }
}

// Inline-Editing für Herstellcode
const editingHerstellcode = ref(null);
const tempHerstellcode = ref("");

async function updateHerstellcode(positionId, newValue) {

  // Sofortige Validierung
  if (!positionId) {
    console.error("Position-ID fehlt");
    toast.error("Fehler: Position-ID fehlt");
    return;
  }

  if (!selectedHeader.value) {
    console.error("Kein Header ausgewählt");
    toast.error("Kein Lieferschein ausgewählt");
    return;
  }

  // Korrekt: 'positionen' statt 'positions'!
  const positions = selectedHeader.value.positionen || selectedHeader.value.positions || [];

  if (!positions || !Array.isArray(positions) || positions.length === 0) {
    console.error("Keine Positionen im Header");
    toast.error("Keine Positionen gefunden");
    return;
  }

  // Versuche sowohl String- als auch Number-Vergleich
  let currentPosition = positions.find(p => p.id == positionId);
  if (!currentPosition) {
    currentPosition = positions.find(p => p.id === String(positionId));
  }
  if (!currentPosition) {
    currentPosition = positions.find(p => p.id === Number(positionId));
  }

  if (!currentPosition) {
    console.error("Position nicht gefunden", positionId);
    toast.error("Position nicht gefunden");
    return;
  }

  // Wert normalisieren
  const normalizedValue = (newValue || "").toString().trim();

  // Prüfen ob sich was geändert hat
  if (normalizedValue === (currentPosition.herstell_code || "").trim()) {
    return;
  }

  try {
    const payload = {
      action: "update_position_field",
      position_id: parseInt(positionId),
      field: "herstell_code",
      value: normalizedValue
    };

    const response = await axios.post(
      `${API_BASE_URL}retouren_v2/index.php`,
      payload,
      {
        withCredentials: true,
        timeout: 10000 // 10 Sekunden Timeout
      }
    );

    if (response.data && response.data.status === "success") {
      // Lokale Aktualisierung
      currentPosition.herstell_code = normalizedValue;
      toast.success("Herstellcode aktualisiert");
    } else {
      const errorMsg = response.data?.message || "Unbekannter Fehler";
      console.error("API Error:", errorMsg);
      toast.error(errorMsg);
    }
  } catch (error) {
    console.error("Herstellcode-Update fehlgeschlagen:", error);

    if (error.code === 'ECONNABORTED') {
      toast.error("Timeout - Server antwortet nicht");
    } else if (error.response) {
      toast.error(`Server-Fehler: ${error.response.status}`);
    } else if (error.request) {
      toast.error("Keine Antwort vom Server");
    } else {
      toast.error("Verbindungsfehler beim Aktualisieren");
    }
  }

}

function startEditHerstellcode(position) {
  editingHerstellcode.value = position.id;
  tempHerstellcode.value = position.herstell_code || "";
}

function cancelEditHerstellcode() {
  editingHerstellcode.value = null;
  tempHerstellcode.value = "";
}

function finishEditHerstellcode(position) {
  if (tempHerstellcode.value.trim() !== (position.herstell_code || '').trim()) {
    updateHerstellcode(position.id, tempHerstellcode.value.trim());
  } else {
    cancelEditHerstellcode();
  }
}

async function fetchIrisCodes() {
  loading.value.irisSearch = true;
  try {
    const response = await axios.get(
      `${API_BASE_URL}retouren_v2/index.php?action=get_iris_codes`,
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      irisCodes.value = response.data.iris_codes;
    }
  } catch (error) {
    console.error("Fehler beim Laden der IRIS-Codes:", error);
  } finally {
    loading.value.irisSearch = false;
  }
}

async function fetchProduktmarken() {
  try {
    const response = await axios.get(
      `${API_BASE_URL}retouren_v2/index.php?action=get_produktmarken`,
      { withCredentials: true }
    );
    if (response.data.status === "success") {
      produktmarken.value = response.data.marken;
    }
  } catch (error) {
    console.error("Fehler beim Laden der Produktmarken:", error);
  }
}

async function fetchProduktgruppen() {
  try {
    const response = await axios.get(
      `${API_BASE_URL}retouren_v2/index.php?action=get_produktgruppen`,
      { withCredentials: true }
    );
    if (response.data.status === "success") {
      produktgruppen.value = response.data.gruppen;
    }
  } catch (error) {
    console.error("Fehler beim Laden der Produktgruppen:", error);
  }
}

// Positionen neu laden (verwendet vom Scanner)
async function loadPositions() {
  if (selectedHeader.value) {
    await loadHeader(selectedHeader.value.id);
  }
}

// Google-Suche öffnen
function openGoogleSearch(searchTerm) {
  if (!searchTerm) {
    toast.warning("Kein Suchbegriff angegeben");
    return;
  }

  const encodedTerm = encodeURIComponent(searchTerm);
  const googleUrl = `https://www.google.com/search?q=${encodedTerm}`;
  window.open(googleUrl, '_blank');

  toast.info(`Google-Suche geöffnet für: ${searchTerm}`);
}

async function onProduktgruppeChanged(produktgruppe_id) {
  if (!produktgruppe_id) {
    symptome.value = [];
    positionData.value.symptom_id = null;
    return;
  }

  try {
    const response = await axios.get(
      `${API_BASE_URL}retouren_v2/index.php?action=get_symptome&produktgruppe_id=${produktgruppe_id}`,
      { withCredentials: true }
    );
    if (response.data.status === "success") {
      symptome.value = response.data.symptome;
    }
  } catch (error) {
    console.error("Fehler beim Laden der Symptome:", error);
  }
}

function onSymptomSelected(symptom_id) {
  if (!symptom_id) return;

  const symptom = symptome.value.find(s => s.id === symptom_id);
  if (symptom) {
    // IRIS-Codes automatisch ausfüllen
    positionData.value.iris_code_1 = symptom.iris_code_1 || '';
    positionData.value.iris_code_2 = symptom.iris_code_2 || '';
    positionData.value.iris_code_3 = symptom.iris_code_3 || '';
    positionData.value.iris_code_4 = symptom.iris_code_4 || '';
  }
}

// ======== Watcher ========
// Automatisch KO setzen wenn IRIS-Code 4 Y/ oder Y7 enthält (nur wenn nicht FR)
watch(() => positionData.value.iris_code_4, (newValue) => {
  if (!positionData.value.fr && newValue && (newValue.includes('Y/') || newValue.includes('Y7'))) {
    positionData.value.s_kennzeichen = 'KO';
  }
});

// Wenn FR aktiviert wird, S-Kennzeichen leeren
watch(() => positionData.value.fr, (newValue) => {
  if (newValue) {
    positionData.value.s_kennzeichen = '';
  } else {
    // Wenn FR deaktiviert wird, setze auf CO (außer es gibt Y7-Code)
    if (positionData.value.iris_code_4 && (positionData.value.iris_code_4.includes('Y/') || positionData.value.iris_code_4.includes('Y7'))) {
      positionData.value.s_kennzeichen = 'KO';
    } else {
      positionData.value.s_kennzeichen = 'CO';
    }
  }
});

// ======== Google-Suche Funktionen ========
function searchGoogleArtikel() {
  if (!positionData.value.artikel) return;

  const searchQuery = encodeURIComponent(positionData.value.artikel);
  const googleUrl = `https://www.google.com/search?q=${searchQuery}`;
  window.open(googleUrl, '_blank');
}

function searchGoogleArtikelFromDialog() {
  // Verwende den eingegebenen Artikel-Wert oder den aktuellen Suchterm
  const searchTerm = positionData.value.artikel || artikelSearchTerm.value;
  if (!searchTerm) return;

  const searchQuery = encodeURIComponent(searchTerm);
  const googleUrl = `https://www.google.com/search?q=${searchQuery}`;
  window.open(googleUrl, '_blank');
}

function searchGoogleArtikelnummer() {
  if (!positionData.value.kaufdatum) return;

  const searchQuery = encodeURIComponent(positionData.value.kaufdatum);
  const googleUrl = `https://www.google.com/search?q=${searchQuery}`;
  window.open(googleUrl, '_blank');
}

function searchGoogleArtikelnummerFromDialog() {
  // Verwende die eingegebene Artikelnummer oder den aktuellen Suchterm
  const searchTerm = positionData.value.kaufdatum || artikelnummerSearchTerm.value;
  if (!searchTerm) return;

  const searchQuery = encodeURIComponent(searchTerm);
  const googleUrl = `https://www.google.com/search?q=${searchQuery}`;
  window.open(googleUrl, '_blank');
}

function searchGoogleArtikelFromTable(searchTerm) {
  if (!searchTerm) return;

  const searchQuery = encodeURIComponent(searchTerm);
  const googleUrl = `https://www.google.com/search?q=${searchQuery}`;
  window.open(googleUrl, '_blank');
}

// ======== Lifecycle ========
onMounted(async () => {
  await fetchHeaders();
  await fetchIrisCodes();
  await fetchProduktmarken();
  await fetchProduktgruppen();
});

//========================================
// EAN-Scanner Funktionalität (Vollständig implementiert)
// ========================================

// Scanner State
const showEanScanner = ref(false);
const scannerReady = ref(false);
const scannerError = ref("");
const scannedEan = ref("");
const mediaStream = ref(null);

// Batch-Scanner Mode
const batchScanMode = ref(false);
const scannedItems = ref([]);
const scanCount = ref(0);
const lastScannedEan = ref("");
const scanTimestamp = ref(0);

// Device Detection (basierend auf InstandsetzungView.vue)
const isMobileDevice = computed(() => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});

const isIOS = computed(() => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
});

// EAN-Scanner öffnen
async function openEanScanner(batchMode = false) {
  if (!ENABLE_EAN_SCANNER) {
    toast.info("EAN-Scanner wird in einer zukünftigen Version verfügbar sein");
    return;
  }

  // Batch-Modus aktivieren wenn gewünscht
  batchScanMode.value = batchMode;
  if (batchMode) {
    scannedItems.value = [];
    scanCount.value = 0;
    toast.info("Batch-Scanner Modus aktiviert - Positionen werden automatisch gespeichert");
  }

  showEanScanner.value = true;
  scannerReady.value = false;
  scannerError.value = "";
  scannedEan.value = "";

  // Warten bis das DOM bereit ist
  await nextTick();

  try {
    // Kamera-Berechtigung anfordern
    const constraints = isIOS.value ? {
      video: {
        facingMode: { exact: 'environment' },
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      }
    } : {
      video: {
        facingMode: 'environment',
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    mediaStream.value = stream;

    // Barcode-Detection starten
    const delay = isMobileDevice.value ? 1000 : 500;
    setTimeout(async () => {
      await startBarcodeDetection();
    }, delay);

  } catch (error) {
    console.error('Kamera-Zugriff fehlgeschlagen:', error);

    if (error.name === 'NotAllowedError') {
      scannerError.value = 'Kamera-Berechtigung wurde verweigert. Bitte erlaube den Kamera-Zugriff.';
    } else if (error.name === 'NotFoundError') {
      scannerError.value = 'Keine Kamera gefunden.';
    } else if (error.name === 'NotSupportedError') {
      scannerError.value = 'Kamera wird von diesem Browser nicht unterstützt.';
    } else {
      scannerError.value = 'Kamera konnte nicht gestartet werden: ' + error.message;
    }
  }
}

// Barcode-Detection starten
async function startBarcodeDetection() {
  try {
    // Quagga2 Configuration
    const config = {
      inputStream: {
        name: "Live",
        type: "LiveStream",
        target: document.querySelector('#ean-scanner'),
        constraints: {
          width: { min: 640 },
          height: { min: 480 },
          facingMode: "environment"
        }
      },
      locator: {
        patchSize: isMobileDevice.value ? "large" : "medium",
        halfSample: !isMobileDevice.value
      },
      numOfWorkers: navigator.hardwareConcurrency || 2,
      decoder: {
        readers: [
          "ean_reader",
          "ean_8_reader",
          "ean_13_reader",
          "code_128_reader",
          "code_39_reader",
          "codabar_reader"
        ]
      },
      locate: true
    };

    // Quagga initialisieren
    await new Promise((resolve, reject) => {
      Quagga.init(config, (err) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      });
    });

    scannerReady.value = true;
    Quagga.start();

    // Event-Listener für gefundene Barcodes
    Quagga.onDetected((result) => {
      const code = result.codeResult.code;
      if (code && code.length >= 8) {
        scannedEan.value = code;
        toast.success(`EAN-Code erkannt: ${code}`);

        // Automatisch verarbeiten nach kurzer Verzögerung
        setTimeout(() => {
          processScannedEan();
        }, 1000);
      }
    });

  } catch (error) {
    console.error('Barcode-Scanner Fehler:', error);
    scannerError.value = 'EAN-Scanner konnte nicht gestartet werden. Versuche die manuelle Eingabe.';
  }
}

// EAN-Scanner schließen
function closeEanScanner() {
  showEanScanner.value = false;
  scannerReady.value = false;
  scannerError.value = "";
  scannedEan.value = "";

  // Batch-Modus zurücksetzen
  if (batchScanMode.value && scanCount.value > 0) {
    toast.success(`Batch-Scan beendet: ${scanCount.value} Positionen hinzugefügt`);
  }
  batchScanMode.value = false;
  scannedItems.value = [];
  scanCount.value = 0;
  lastScannedEan.value = "";

  try {
    // Quagga stoppen
    if (Quagga.initialized) {
      Quagga.stop();
      Quagga.offDetected();
    }

    // Media Stream stoppen
    if (mediaStream.value) {
      mediaStream.value.getTracks().forEach(track => track.stop());
      mediaStream.value = null;
    }
  } catch (error) {
    console.error('Fehler beim Schließen des Scanners:', error);
  }
}

// Gescannte EAN verarbeiten
async function processScannedEan() {
  if (!scannedEan.value || scannedEan.value.trim() === "") {
    toast.warning("Bitte gib eine EAN-Nummer ein");
    return;
  }

  const ean = scannedEan.value.trim();

  // Duplikate in schneller Folge verhindern
  if (lastScannedEan.value === ean && Date.now() - scanTimestamp.value < 2000) {
    return;
  }

  lastScannedEan.value = ean;
  scanTimestamp.value = Date.now();

  const artikel = await searchArticleByEan(ean);

  if (artikel) {
    if (batchScanMode.value) {
      // Batch-Modus: Automatisch speichern und weiter scannen
      await saveScannedPosition(artikel, ean);
    } else {
      // Einzelner Scan: Dialog öffnen für weitere Eingaben
      positionData.value.artikel = artikel.artikel;
      positionData.value.kaufdatum = artikel.artikelnummer;
      positionData.value.herstell_code = artikel.herstell_code || '';

      closeEanScanner();
      positionDialog.value = true;

      // Focus auf Menge
      setTimeout(() => {
        const mengeField = document.querySelector('#menge-field input');
        if (mengeField) mengeField.focus();
      }, 300);
    }
  }

  // EAN-Feld für nächsten Scan leeren
  scannedEan.value = "";
}

// Position aus Scanner automatisch speichern
async function saveScannedPosition(artikel, ean) {
  try {
    loading.value = true;

    const positionData = {
      artikel: artikel.artikelbezeichnung,
      kaufdatum: artikel.artikelnummer,
      herstellcode: artikel.herstellcode || '',
      menge: 1, // Standard-Menge beim Scannen
      s_kennzeichen: 'CO', // Standard S-Kennzeichen
      fr: false,
      iris_code_4: artikel.herstellcode || '',
      bemerkung: `Gescannt: EAN ${ean}`
    };

    const response = await fetch('/backend/api/retouren_v2/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      body: JSON.stringify({
        action: 'save_position',
        position_data: positionData,
        retoure_id: route.params.id
      })
    });

    const data = await response.json();

    if (data.success) {
      scanCount.value++;
      scannedItems.value.push({
        ean: ean,
        artikel: artikel.artikelbezeichnung,
        artikelnummer: artikel.artikelnummer,
        timestamp: new Date().toLocaleTimeString()
      });

      toast.success(`✓ ${artikel.artikelbezeichnung} hinzugefügt (${scanCount.value})`);

      // Tabelle aktualisieren
      await loadPositions();

      // Bereit für nächsten Scan
      setTimeout(() => {
        if (showEanScanner.value) {
          toast.info("Bereit für nächsten Scan...", { timeout: 1000 });
        }
      }, 1000);

    } else {
      throw new Error(data.message || 'Fehler beim Speichern');
    }

  } catch (error) {
    console.error('Fehler beim Speichern der gescannten Position:', error);
    toast.error(`Fehler beim Speichern: ${error.message}`);
  } finally {
    loading.value = false;
  }
}

async function searchArticleByEan(ean) {
  try {
    loading.value = true;
    const response = await fetch('/backend/api/retouren_v2/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      body: JSON.stringify({
        action: 'search_ean',
        ean: ean
      })
    });

    const data = await response.json();

    if (data.success && data.artikel) {
      const artikel = data.artikel;

      // Artikel in das Positionsformular übernehmen
      positionDialog.value.artikelbezeichnung = artikel.artikelbezeichnung;
      positionDialog.value.artikelnummer = artikel.artikelnummer;
      positionDialog.value.herstellcode = artikel.herstellcode || '';

      // Optional: EAN-Code auch anzeigen
      if (artikel.ean) {
        toast.success(`Artikel gefunden über EAN: ${artikel.ean}`);
      } else {
        toast.success("Artikel gefunden!");
      }

      // Focus auf Menge für direkte Eingabe
      setTimeout(() => {
        const mengeField = document.querySelector('#menge-field input');
        if (mengeField) mengeField.focus();
      }, 300);

      return artikel;

    } else {
      // Kein Artikel gefunden - Fehlermeldung und Google-Suche anbieten
      const message = data.message || `Kein Artikel mit EAN "${ean}" gefunden.`;
      toast.warning(message);

      // EAN in das Eingabefeld übernehmen für weitere Bearbeitung
      positionDialog.value.herstellcode = ean;
      positionDialog.value.artikelbezeichnung = `Unbekannter Artikel (EAN: ${ean})`;

      // Google-Suche anbieten
      setTimeout(() => {
        const shouldSearch = confirm(`Artikel mit EAN "${ean}" wurde nicht gefunden.\n\nMöchten Sie eine Google-Suche durchführen?`);
        if (shouldSearch) {
          openGoogleSearch(ean);
        }
      }, 500);

      return null;
    }

  } catch (error) {
    console.error('EAN-Suche Fehler:', error);
    toast.error('Fehler bei der EAN-Suche: ' + error.message);
    return null;
  } finally {
    loading.value = false;
  }
}

// Manuelle EAN-Eingabe verarbeiten
async function processManualEan() {
  if (!scannedEan.value || scannedEan.value.trim() === "") {
    toast.warning("Bitte gib eine EAN-Nummer ein");
    return;
  }

  await processScannedEan();
}

// EAN-Scanner Button Handler (Neue vollständige Implementierung)
function handleEanScannerButton() {
  if (!ENABLE_EAN_SCANNER) {
    toast.info("EAN-Scanner wird in einer zukünftigen Version verfügbar sein");
    return;
  }

  openEanScanner();
}
</script>

<style scoped>
.retouren-v2-container {
  max-width: 1600px;
  margin: 0 auto;
}

.overview-card,
.header-info-card,
.positionen-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.positionen-table :deep(th) {
  background-color: rgba(255, 255, 255, 0.05);
  font-weight: 600;
}

.positionen-table :deep(td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.iris-codes-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* ======== EAN-Scanner Styles ======== */
.scanner-container {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.scanner-viewport {
  width: 100%;
  height: 300px;
  border: 2px solid #4CAF50;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.scanner-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 70%;
  height: 20%;
  border: 2px solid #FF5722;
  border-radius: 4px;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3);
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .scanner-viewport {
    height: 250px;
  }

  .scanner-frame {
    width: 80%;
    height: 25%;
  }
}
</style>
